import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  effect,
  input,
  output,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { HourglassSpinnerComponent } from '@shared/hourglass-spinner/hourglass-spinner.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import {
  MESSAGE_TYPES,
  ChatMessage,
  MessageType,
} from '@core/constants/guided-course-creation.constants';

export { MESSAGE_TYPES, ChatMessage, MessageType };

@Component({
  selector: 'app-outline-chat',
  templateUrl: './outline-chat.component.html',
  styleUrls: ['./outline-chat.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    TranslateModule,
    TooltipButtonComponent,
    HourglassSpinnerComponent,
  ],
})
export class OutlineChatComponent {
  readonly MESSAGE_TYPES = MESSAGE_TYPES;

  readonly isLoading = input(false);

  readonly previewApproved = output<ChatMessage>();
  readonly previewCancelled = output<void>();

  readonly messages = signal<ChatMessage[]>([]);
  readonly pendingPreview = signal<ChatMessage | null>(null);
  readonly isFetching = signal(false);

  private readonly messagesContainer =
    viewChild<ElementRef<HTMLElement>>('messagesContainer');

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gccService = inject(GuidedCourseCreationService);

  readonly messageForm = this.fb.nonNullable.group({
    content: ['', Validators.required],
  });

  constructor() {
    effect(() => {
      this.messages();
      this.pendingPreview();
      this.scrollToBottom();
    });

    effect(() => {
      const method =
        this.isLoading() || this.isFetching() ? 'disable' : 'enable';
      this.messageForm.controls.content[method]();
    });
  }

  sendMessage(): void {
    if (this.messageForm.invalid || this.isLoading() || this.isFetching())
      return;

    const content = this.messageForm.getRawValue().content.trim();
    if (!content) return;

    this.messages.update(msgs => [
      ...msgs,
      { type: MESSAGE_TYPES.USER, content },
    ]);
    this.messageForm.reset();

    this.isFetching.set(true);
    this.gccService
      .sendChatMessage(content)
      .pipe(
        finalize(() => this.isFetching.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(preview => {
        this.pendingPreview.set(preview);
      });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  approvePreview(): void {
    const preview = this.pendingPreview();
    if (!preview) return;

    this.previewApproved.emit(preview);
    this.messages.update(msgs => [
      ...msgs,
      { type: MESSAGE_TYPES.AI, content: preview.content },
    ]);
    this.pendingPreview.set(null);
  }

  cancelPreview(): void {
    this.previewCancelled.emit();
    this.pendingPreview.set(null);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const container = this.messagesContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }
}
